import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { FiRefreshCw, FiExternalLink, FiChevronDown, FiChevronUp, FiSearch, FiCheckCircle, FiClock, FiAlertCircle } from "react-icons/fi";
import { HiOutlineCash, HiOutlineCurrencyDollar } from "react-icons/hi";
import Get from "../services/Get";
import { isApiFailure, unwrapList } from "../services/providerData";

const EarnPlans = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [monthlyPlans, setMonthlyPlans] = useState([]);
  const [scipPlans, setScipPlans] = useState([]);
  const [superStakes, setSuperStakes] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchPlans = async () => {
    setLoading(true);
    setError("");
    try {
      const results = await Promise.allSettled([
        Get.MonthlyRewardPlans({ per_page: 50 }),
        Get.ScipPlans({ per_page: 50 }),
        Get.SuperStakes({ per_page: 50 }),
      ]);

      const responses = results.map((r) => (r.status === "fulfilled" ? r.value : null));
      const lists = (response, key) => (response && !isApiFailure(response) ? unwrapList(response, key) : []);

      setMonthlyPlans(lists(responses[0], "plans"));
      setScipPlans(lists(responses[1], "plans"));
      setSuperStakes(lists(responses[2], "stakes"));

      const hasFail = results.some((r) => r.status === "rejected") || responses.some((res) => res && isApiFailure(res));
      if (hasFail && !responses[0] && !responses[1] && !responses[2]) {
        setError("Unable to load earn plans. Please try refreshing or check your connection.");
      }
    } catch (err) {
      setError(err?.message || "Failed to load earn plans.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Format all plans into a unified list
  const allRows = useMemo(() => {
    const monthly = monthlyPlans.map((p, idx) => ({
      uid: `monthly-${p.id || p.plan_no || idx}`,
      id: p.id,
      category: "Monthly Reward",
      planNo: p.plan_no || p.plan_code || `MRP-${p.id || idx + 1}`,
      asset: p.asset || p.asset_symbol || (Array.isArray(p.supported_assets) ? p.supported_assets.join(", ") : "USDT"),
      amount: p.amount || p.principal || p.deposit_amount ? `${Number(p.amount || p.principal || p.deposit_amount).toLocaleString()} ${p.asset || "USDT"}` : "—",
      rate: p.monthly_rate ? `${(Number(p.monthly_rate) * 100).toFixed(2)}% monthly` : (p.rate ? `${p.rate}%` : "—"),
      term: p.duration_days ? `${p.duration_days} days` : (p.term_months ? `${p.term_months} mos` : "—"),
      status: (p.status || "ACTIVE").toUpperCase(),
      reward: p.monthly_reward || p.reward_amount ? `${p.monthly_reward || p.reward_amount} ${p.asset || ""}` : "—",
      createdAt: p.created_at || p.start_date || "—",
      maturityAt: p.maturity_at || p.end_date || "—",
      raw: p,
    }));

    const scip = scipPlans.map((p, idx) => ({
      uid: `scip-${p.id || p.plan_no || idx}`,
      id: p.id,
      category: "SCIP",
      planNo: p.plan_no || `SCIP-${p.id || idx + 1}`,
      asset: p.asset_symbol || p.asset || "USDT",
      amount: p.quantity || p.monthly_quantity || p.amount ? `${Number(p.quantity || p.monthly_quantity || p.amount).toLocaleString()} ${p.asset_symbol || ""}/mo` : "—",
      rate: p.term_months ? `${p.term_months} Months Term` : "Fixed Qty",
      term: p.term_months ? `${p.term_months} months` : "—",
      status: (p.status || "ACTIVE").toUpperCase(),
      reward: p.reward_amount ? `${p.reward_amount} ${p.asset_symbol || ""}` : (p.due_day ? `Due day: ${p.due_day}` : "—"),
      createdAt: p.created_at || "—",
      maturityAt: p.maturity_at || p.next_due_date || "—",
      raw: p,
    }));

    const stakes = superStakes.map((s, idx) => ({
      uid: `stake-${s.id || idx}`,
      id: s.id,
      category: "Super Stake Pro",
      planNo: s.plan_no || `STK-${s.id || idx + 1}`,
      asset: s.asset || s.symbol || s.coin || "—",
      amount: s.amount ? `${Number(s.amount).toLocaleString()} ${s.asset || s.symbol || ""}` : "—",
      rate: s.reward_rate ? `${(Number(s.reward_rate) * 100).toFixed(2)}% Term Reward` : (s.apy ? `${s.apy}% APY` : "—"),
      term: s.term_days ? `${s.term_days} days` : "—",
      status: (s.status || "ACTIVE").toUpperCase(),
      reward: s.reward_amount || s.estimated_reward ? `${s.reward_amount || s.estimated_reward} ${s.asset || ""}` : "—",
      createdAt: s.created_at || "—",
      maturityAt: s.maturity_at || s.unlock_date || "—",
      raw: s,
    }));

    return [...monthly, ...scip, ...stakes];
  }, [monthlyPlans, scipPlans, superStakes]);

  // Counts
  const stats = useMemo(() => {
    const total = allRows.length;
    const active = allRows.filter((r) => r.status === "ACTIVE").length;
    const monthlyActive = monthlyPlans.filter((p) => (p.status || "ACTIVE").toUpperCase() === "ACTIVE").length;
    const scipActive = scipPlans.filter((p) => (p.status || "ACTIVE").toUpperCase() === "ACTIVE").length;
    const stakeActive = superStakes.filter((s) => (s.status || "ACTIVE").toUpperCase() === "ACTIVE").length;
    return { total, active, monthlyActive, scipActive, stakeActive };
  }, [allRows, monthlyPlans, scipPlans, superStakes]);

  // Filtered rows
  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      if (activeCategory !== "All" && row.category !== activeCategory) return false;
      if (statusFilter !== "All" && row.status !== statusFilter) return false;
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const matchesPlan = String(row.planNo).toLowerCase().includes(query);
        const matchesAsset = String(row.asset).toLowerCase().includes(query);
        const matchesCategory = String(row.category).toLowerCase().includes(query);
        if (!matchesPlan && !matchesAsset && !matchesCategory) return false;
      }
      return true;
    });
  }, [allRows, activeCategory, statusFilter, searchTerm]);

  const statusBadge = (status) => {
    if (status === "ACTIVE") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-500 border border-emerald-500/20">
          <FiCheckCircle className="w-3 h-3" /> Active
        </span>
      );
    }
    if (status === "COMPLETED") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2.5 py-0.5 text-xs font-medium text-blue-400 border border-blue-500/20">
          <FiClock className="w-3 h-3" /> Completed
        </span>
      );
    }
    if (status === "PENDING") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-400 border border-amber-500/20">
          <FiClock className="w-3 h-3" /> Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-zinc-500/10 px-2.5 py-0.5 text-xs font-medium text-zinc-400 border border-zinc-500/20">
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-200 dark:border-[#2b3139] pb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <HiOutlineCash className="w-7 h-7 text-[#f0b90b]" />
            My Earn Plans
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-[#848e9c]">
            Track and manage your active and past subscriptions for Monthly Reward, SCIP, and Super Stake Pro.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={fetchPlans}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-[#303844] text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#222731] transition disabled:opacity-50"
            title="Refresh plans"
          >
            <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <Link
            to="/simpleEarn"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg bg-[#f0b90b] text-[#181a20] hover:bg-[#fcd535] transition shadow-sm font-semibold"
          >
            Explore Earn Products <FiExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-500">
          <FiAlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl border border-gray-200 dark:border-[#2b3139] bg-white dark:bg-[#181a20] shadow-sm">
          <span className="text-xs text-gray-500 dark:text-[#848e9c] block font-medium">Total Active Plans</span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white mt-1 block">
            {loading ? "…" : stats.active}
          </span>
          <span className="text-[11px] text-gray-400 mt-1 block">{stats.total} total subscriptions</span>
        </div>

        <div className="p-4 rounded-xl border border-gray-200 dark:border-[#2b3139] bg-white dark:bg-[#181a20] shadow-sm">
          <span className="text-xs text-gray-500 dark:text-[#848e9c] block font-medium">Monthly Reward</span>
          <span className="text-2xl font-bold text-emerald-500 mt-1 block">
            {loading ? "…" : stats.monthlyActive}
          </span>
          <span className="text-[11px] text-gray-400 mt-1 block">{monthlyPlans.length} plans enrolled</span>
        </div>

        <div className="p-4 rounded-xl border border-gray-200 dark:border-[#2b3139] bg-white dark:bg-[#181a20] shadow-sm">
          <span className="text-xs text-gray-500 dark:text-[#848e9c] block font-medium">SCIP Plans</span>
          <span className="text-2xl font-bold text-[#f0b90b] mt-1 block">
            {loading ? "…" : stats.scipActive}
          </span>
          <span className="text-[11px] text-gray-400 mt-1 block">{scipPlans.length} plans enrolled</span>
        </div>

        <div className="p-4 rounded-xl border border-gray-200 dark:border-[#2b3139] bg-white dark:bg-[#181a20] shadow-sm">
          <span className="text-xs text-gray-500 dark:text-[#848e9c] block font-medium">Super Stake Pro</span>
          <span className="text-2xl font-bold text-indigo-400 mt-1 block">
            {loading ? "…" : stats.stakeActive}
          </span>
          <span className="text-[11px] text-gray-400 mt-1 block">{superStakes.length} stakes enrolled</span>
        </div>
      </div>

      {/* Tabs and Filters */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 border-b border-gray-200 dark:border-[#2b3139] pb-3">
        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 md:pb-0">
          {["All", "Monthly Reward", "SCIP", "Super Stake Pro"].map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-[#f0b90b] text-[#181a20] font-semibold"
                  : "text-gray-600 dark:text-[#848e9c] hover:bg-gray-100 dark:hover:bg-[#222731]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-56">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by plan or coin…"
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-[#303844] bg-white dark:bg-[#181a20] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#f0b90b]"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-[#303844] bg-white dark:bg-[#181a20] text-gray-900 dark:text-white focus:outline-none focus:border-[#f0b90b]"
          >
            <option value="All">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETED">Completed</option>
            <option value="PENDING">Pending</option>
          </select>
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-[#2b3139] bg-white dark:bg-[#181a20] shadow-sm">
        <table className="w-full min-w-[760px] border-collapse text-left text-xs">
          <thead className="bg-gray-50 dark:bg-[#20252e] text-gray-500 dark:text-[#848e9c] border-b border-gray-200 dark:border-[#2b3139]">
            <tr>
              <th className="py-3 px-4 font-medium">Plan / Product</th>
              <th className="py-3 px-4 font-medium">Coin / Asset</th>
              <th className="py-3 px-4 font-medium">Commitment / Amount</th>
              <th className="py-3 px-4 font-medium">Rate / Term</th>
              <th className="py-3 px-4 font-medium">Duration</th>
              <th className="py-3 px-4 font-medium">Status</th>
              <th className="py-3 px-4 font-medium text-right">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-[#2b3139]">
            {loading && (
              <tr>
                <td colSpan="7" className="py-12 text-center text-sm text-gray-400">
                  <div className="inline-flex items-center gap-2">
                    <FiRefreshCw className="animate-spin w-4 h-4 text-[#f0b90b]" />
                    Loading your Earn plans…
                  </div>
                </td>
              </tr>
            )}

            {!loading && filteredRows.map((row) => (
              <React.Fragment key={row.uid}>
                <tr className="hover:bg-gray-50 dark:hover:bg-[#1f242c] transition">
                  <td className="py-3.5 px-4">
                    <div className="font-medium text-gray-900 dark:text-white">{row.planNo}</div>
                    <div className="text-[11px] text-gray-400">{row.category}</div>
                  </td>
                  <td className="py-3.5 px-4 font-semibold text-gray-900 dark:text-white">
                    {row.asset}
                  </td>
                  <td className="py-3.5 px-4 text-gray-800 dark:text-gray-200 font-medium">
                    {row.amount}
                  </td>
                  <td className="py-3.5 px-4 text-emerald-500 font-medium">
                    {row.rate}
                  </td>
                  <td className="py-3.5 px-4 text-gray-600 dark:text-gray-300">
                    {row.term}
                  </td>
                  <td className="py-3.5 px-4">
                    {statusBadge(row.status)}
                  </td>
                  <td className="py-3.5 px-4 text-right">
                    <button
                      type="button"
                      onClick={() => setExpandedRow(expandedRow === row.uid ? null : row.uid)}
                      className="inline-flex items-center gap-1 text-gray-500 hover:text-[#f0b90b] transition"
                      aria-label="Toggle details"
                    >
                      {expandedRow === row.uid ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                    </button>
                  </td>
                </tr>

                {expandedRow === row.uid && (
                  <tr className="bg-gray-50/70 dark:bg-[#161a22]">
                    <td colSpan="7" className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-600 dark:text-[#aeb8c6] border border-gray-200 dark:border-[#2b3139] rounded-lg p-3 bg-white dark:bg-[#1b2029]">
                        <div>
                          <span className="block text-gray-400 text-[11px]">Product Type</span>
                          <span className="font-medium text-gray-900 dark:text-white">{row.category}</span>
                        </div>
                        <div>
                          <span className="block text-gray-400 text-[11px]">Plan Code / ID</span>
                          <span className="font-medium text-gray-900 dark:text-white">{row.planNo}</span>
                        </div>
                        <div>
                          <span className="block text-gray-400 text-[11px]">Created Date</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {row.createdAt !== "—" ? new Date(row.createdAt).toLocaleDateString() : "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-gray-400 text-[11px]">Maturity / Next Date</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {row.maturityAt !== "—" && !isNaN(Date.parse(row.maturityAt))
                              ? new Date(row.maturityAt).toLocaleDateString()
                              : row.maturityAt}
                          </span>
                        </div>
                        {row.reward !== "—" && (
                          <div>
                            <span className="block text-gray-400 text-[11px]">Estimated Reward / Info</span>
                            <span className="font-medium text-emerald-500">{row.reward}</span>
                          </div>
                        )}
                        {row.raw?.due_day && (
                          <div>
                            <span className="block text-gray-400 text-[11px]">Monthly Due Day</span>
                            <span className="font-medium text-gray-900 dark:text-white">Day {row.raw.due_day}</span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}

            {!loading && filteredRows.length === 0 && (
              <tr>
                <td colSpan="7" className="py-12 text-center text-gray-400">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <HiOutlineCurrencyDollar className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {searchTerm || statusFilter !== "All" || activeCategory !== "All"
                        ? "No earn plans match your filter criteria."
                        : "You haven't subscribed to any Earn plans yet."}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-[#848e9c]">
                      Earn rewards on your crypto with flexible and fixed products.
                    </p>
                    <Link
                      to="/simpleEarn"
                      className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#f0b90b] text-[#181a20] text-xs font-semibold hover:bg-[#fcd535] transition shadow-sm"
                    >
                      Subscribe to Earn Plans
                    </Link>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EarnPlans;